import { createSlice, PayloadAction, type Draft } from '@reduxjs/toolkit';
import { StoreKey } from '@/configs/storeKeys';
import {
	CreatorOptions,
	CreatorPricingState,
	CreatorSnapshot,
	CreatorState,
	CreatorStep,
	ViewType,
	LayerType,
	CreatorStage,
} from '@/types/creator';
import { PricingBreakdown } from '@/types/pricing';
import {
	TextElement,
	SurfaceTextMap,
	ImageElement,
	SurfaceImageMap,
} from '@/types/personalization';
import { ProjectUpload } from '@/types/uploads';
import { cloneSurfaceTextMap, ensureTextElementDefaults } from '@/utils/textElements';
import { cloneSurfaceImageMap, ensureImageElementDefaults } from '@/utils/imageElements';
import { BoxModelStyle } from '@/three/types';
import { parseCartonDimensionString, parseDimensionString } from '@/utils/dimensions';
import { getProductSurfaces } from '@/data/productSurfaces';

import { loadCreatorSnapshot } from '@/store/persistence/creatorStorage';

const createInitialPricingState = (): CreatorPricingState => ({
	status: 'idle',
	breakdown: null,
	error: null,
	requiresQuote: false,
});

const DEFAULT_AUTOSAVE_INTERVAL = 3000;
const DEFAULT_UPLOAD_MAX_SIZE = 5 * 1024 * 1024;

const createDefaultSettings = () => ({
	autosaveInterval: DEFAULT_AUTOSAVE_INTERVAL,
	uploadMaxSize: DEFAULT_UPLOAD_MAX_SIZE,
	fallbackTemplateIds: [] as string[],
});

type SurfaceElementDescriptor = {
	type: 'text' | 'image';
	element: TextElement | ImageElement;
};

const collectSurfaceElements = (
	state: CreatorState | Draft<CreatorState>,
	surfaceId: string
): SurfaceElementDescriptor[] => {
	const textList = state.textElements[surfaceId] ?? [];
	const imageList = state.imageElements[surfaceId] ?? [];
	return [
		...textList.map((element) => ({ type: 'text' as const, element })),
		...imageList.map((element) => ({ type: 'image' as const, element })),
	];
};

const normalizeSurfaceLayers = (
	state: CreatorState | Draft<CreatorState>,
	surfaceId: string
) => {
	const combined = collectSurfaceElements(state, surfaceId);
	if (combined.length === 0) {
		return;
	}

	combined.sort(
		(a, b) => (a.element.layerIndex ?? 0) - (b.element.layerIndex ?? 0)
	);

	combined.forEach(({ element }, index) => {
		element.layerIndex = index;
	});

	state.textElements[surfaceId] = combined
		.filter((item) => item.type === 'text')
		.map((item) => item.element as TextElement);
	state.imageElements[surfaceId] = combined
		.filter((item) => item.type === 'image')
		.map((item) => item.element as ImageElement);
};

const normalizeAllSurfaceLayers = (state: CreatorState | Draft<CreatorState>) => {
	const surfaceIds = new Set<string>([
		...Object.keys(state.textElements),
		...Object.keys(state.imageElements),
	]);
	surfaceIds.forEach((surfaceId) => normalizeSurfaceLayers(state, surfaceId));
};

const getNextLayerIndex = (
	state: CreatorState | Draft<CreatorState>,
	surfaceId: string
) => {
	const combined = collectSurfaceElements(state, surfaceId);
	if (combined.length === 0) return 0;
	const maxIndex = combined.reduce(
		(acc, item) => Math.max(acc, item.element.layerIndex ?? 0),
		0
	);
	return maxIndex + 1;
};

const TAPE_REPEAT_ATTRIBUTE_ID = 'tape.repeatCount';
const TAPE_REPEAT_ID_SUFFIX = '__r';
const isTapeSurfaceId = (surfaceId: string) =>
	surfaceId === 'external-front' || surfaceId === 'internal-front';

const clamp = (value: number, min: number, max: number) =>
	Math.min(Math.max(value, min), max);
const clamp01 = (value: number) => clamp(value, 0, 1);

const parsePositiveInt = (raw: unknown): number => {
	if (raw === null || raw === undefined) return 0;
	if (typeof raw === 'number' && Number.isFinite(raw)) return Math.floor(raw);
	const parsed = Number.parseInt(String(raw), 10);
	return Number.isFinite(parsed) ? parsed : 0;
};

const getPresetAttributeValue = (
	value: string | number | (string | number)[]
): string | number | null => {
	if (Array.isArray(value)) {
		return (value[0] as string | number | undefined) ?? null;
	}

	return value ?? null;
};

const parseSizeForBoxStyle = (
	style: BoxModelStyle,
	rawValue?: string | number | null
) => {
	if (style === 'box-flap' || style === 'box-die-cut') {
		return parseCartonDimensionString(rawValue);
	}

	return parseDimensionString(rawValue);
};

const getTapeRepeatCount = (state: CreatorState | Draft<CreatorState>) => {
	if (state.selectedProductBoxStyle !== 'tape') return 1;
	const parsed = parsePositiveInt(state.configuration?.[TAPE_REPEAT_ATTRIBUTE_ID]);
	return parsed >= 1 ? parsed : 1;
};

const getTapeSegmentIndex = (x: number, repeatCount: number) =>
	clamp(Math.floor(clamp01(x) * repeatCount), 0, Math.max(repeatCount - 1, 0));

const getTapeLocalX = (x: number, repeatIndex: number, repeatCount: number) =>
	clamp(clamp01(x) * repeatCount - clamp(repeatIndex, 0, repeatCount - 1), 0, 1);

const getTapeGlobalX = (localX: number, repeatIndex: number, repeatCount: number) =>
	clamp01((clamp(repeatIndex, 0, repeatCount - 1) + clamp(localX, 0, 1)) / repeatCount);

const getUniqueRepeatId = (baseId: string, repeatIndex: number, usedIds: Set<string>) => {
	const prefix = `${baseId}${TAPE_REPEAT_ID_SUFFIX}${repeatIndex}`;
	if (!usedIds.has(prefix)) return prefix;
	let counter = 1;
	while (usedIds.has(`${prefix}_${counter}`)) counter += 1;
	return `${prefix}_${counter}`;
};

const normalizeTapeRepeatGroups = (state: CreatorState | Draft<CreatorState>) => {
	if (state.selectedProductBoxStyle !== 'tape') return;
	const repeatCount = getTapeRepeatCount(state);

	const normalizeTextSurface = (surfaceId: string) => {
		const list = state.textElements[surfaceId];
		if (!list || list.length === 0) return;

		if (repeatCount <= 1) {
			const groups = new Map<string, TextElement[]>();
			list.forEach((element) => {
				const groupId = element.repeatGroupId;
				if (!groupId) return;
				const entries = groups.get(groupId) ?? [];
				entries.push(element);
				groups.set(groupId, entries);
			});

			if (groups.size === 0) return;

			const keep = new Set<string>();
			groups.forEach((elements) => {
				const preferred =
					elements.find((element) => (element.repeatIndex ?? 0) === 0) ?? elements[0];
				if (preferred) keep.add(preferred.id);
			});

			state.textElements[surfaceId] = list.filter(
				(element) => !element.repeatGroupId || keep.has(element.id)
			);
			normalizeSurfaceLayers(state, surfaceId);
			return;
		}

		const usedIds = new Set<string>(list.map((el) => el.id));
		const groups = new Map<string, TextElement[]>();
		list.forEach((element) => {
			const groupId = element.repeatGroupId ?? element.id;
			const entries = groups.get(groupId) ?? [];
			entries.push(element);
			groups.set(groupId, entries);
		});

		const next: TextElement[] = [];
		groups.forEach((elements, groupId) => {
			const base = elements[0];
			const baseIndex =
				typeof base.repeatIndex === 'number'
					? clamp(base.repeatIndex, 0, repeatCount - 1)
					: getTapeSegmentIndex(base.position.x, repeatCount);
			let localX = getTapeLocalX(base.position.x, baseIndex, repeatCount);
			if (typeof base.repeatIndex !== 'number' && (localX < 0.0001 || localX > 0.9999)) {
				localX = 0.5;
			}
			const y = clamp01(base.position.y);

			for (let i = 0; i < repeatCount; i += 1) {
				const existing =
					elements.find((el) => (el.repeatIndex ?? -1) === i) ??
					(i === baseIndex ? base : null);
				if (existing) {
					next.push({
						...existing,
						repeatGroupId: groupId,
						repeatIndex: i,
						position: { x: getTapeGlobalX(localX, i, repeatCount), y },
					});
					continue;
				}

				const id = getUniqueRepeatId(groupId, i, usedIds);
				usedIds.add(id);
				next.push({
					...base,
					id,
					repeatGroupId: groupId,
					repeatIndex: i,
					position: { x: getTapeGlobalX(localX, i, repeatCount), y },
				});
			}
		});

		state.textElements[surfaceId] = next;
		normalizeSurfaceLayers(state, surfaceId);
	};

	const normalizeImageSurface = (surfaceId: string) => {
		const list = state.imageElements[surfaceId];
		if (!list || list.length === 0) return;

		if (repeatCount <= 1) {
			const groups = new Map<string, ImageElement[]>();
			list.forEach((element) => {
				const groupId = element.repeatGroupId;
				if (!groupId) return;
				const entries = groups.get(groupId) ?? [];
				entries.push(element);
				groups.set(groupId, entries);
			});

			if (groups.size === 0) return;

			const keep = new Set<string>();
			groups.forEach((elements) => {
				const preferred =
					elements.find((element) => (element.repeatIndex ?? 0) === 0) ?? elements[0];
				if (preferred) keep.add(preferred.id);
			});

			state.imageElements[surfaceId] = list.filter(
				(element) => !element.repeatGroupId || keep.has(element.id)
			);
			normalizeSurfaceLayers(state, surfaceId);
			return;
		}

		const usedIds = new Set<string>(list.map((el) => el.id));
		const groups = new Map<string, ImageElement[]>();
		list.forEach((element) => {
			const groupId = element.repeatGroupId ?? element.id;
			const entries = groups.get(groupId) ?? [];
			entries.push(element);
			groups.set(groupId, entries);
		});

		const next: ImageElement[] = [];
		groups.forEach((elements, groupId) => {
			const base = elements[0];
			const baseIndex =
				typeof base.repeatIndex === 'number'
					? clamp(base.repeatIndex, 0, repeatCount - 1)
					: getTapeSegmentIndex(base.position.x, repeatCount);
			let localX = getTapeLocalX(base.position.x, baseIndex, repeatCount);
			if (typeof base.repeatIndex !== 'number' && (localX < 0.0001 || localX > 0.9999)) {
				localX = 0.5;
			}
			const y = clamp01(base.position.y);

			for (let i = 0; i < repeatCount; i += 1) {
				const existing =
					elements.find((el) => (el.repeatIndex ?? -1) === i) ??
					(i === baseIndex ? base : null);
				if (existing) {
					next.push({
						...existing,
						repeatGroupId: groupId,
						repeatIndex: i,
						position: { x: getTapeGlobalX(localX, i, repeatCount), y },
					});
					continue;
				}

				const id = getUniqueRepeatId(groupId, i, usedIds);
				usedIds.add(id);
				next.push({
					...base,
					id,
					repeatGroupId: groupId,
					repeatIndex: i,
					position: { x: getTapeGlobalX(localX, i, repeatCount), y },
				});
			}
		});

		state.imageElements[surfaceId] = next;
		normalizeSurfaceLayers(state, surfaceId);
	};

	const surfaceIds = new Set<string>([
		...Object.keys(state.textElements),
		...Object.keys(state.imageElements),
	]);
	surfaceIds.forEach((surfaceId) => {
		if (!isTapeSurfaceId(surfaceId)) return;
		normalizeTextSurface(surfaceId);
		normalizeImageSurface(surfaceId);
	});
};

const enforceExternalForPaperBag = (
	state: CreatorState | Draft<CreatorState>,
	patch: Partial<CreatorOptions>
) => {
	if (state.selectedProductBoxStyle !== 'paper-bag' && state.selectedProductBoxStyle !== 'tape') {
		return patch;
	}

	const next: Partial<CreatorOptions> = { ...patch };
	const surfaces = getProductSurfaces(state.selectedProductTypeId, state.selectedProductBoxStyle);

	// Wymuś warstwę zewnętrzną i zamknięty model (brak warstwy wewnętrznej).
	const desiredLayer =
		next.layerType ?? state.options.layerType ?? LayerType.EXTERNAL;
	if (desiredLayer === LayerType.INTERNAL) {
		next.layerType = LayerType.EXTERNAL;
	}
	next.isBoxOpened = false;

	// Upewnij się, że aktywna powierzchnia należy do grupy external.
	const nextSurfaceId = next.activeSurfaceId ?? state.options.activeSurfaceId;
	const hasExternalSurface =
		nextSurfaceId &&
		surfaces.some(
			(surface) => surface.id === nextSurfaceId && surface.group === 'external'
		);
	if (!hasExternalSurface) {
		const fallback = surfaces.find((surface) => surface.group === 'external');
		next.activeSurfaceId = fallback?.id ?? null;
		next.activeSurfaceAxes = fallback?.axes ?? next.activeSurfaceAxes ?? null;
	}

	return next;
};

const applySurfaceLayerOperation = (
	state: CreatorState | Draft<CreatorState>,
	{
		surfaceId,
		id,
		elementType,
		operation,
	}: {
		surfaceId: string;
		id: string;
		elementType: 'text' | 'image';
		operation: 'forward' | 'backward' | 'front' | 'back';
	}
) => {
	normalizeSurfaceLayers(state, surfaceId);
	const combined = collectSurfaceElements(state, surfaceId).sort(
		(a, b) => (a.element.layerIndex ?? 0) - (b.element.layerIndex ?? 0)
	);
	if (combined.length <= 1) return;

	const currentIndex = combined.findIndex(
		(item) => item.type === elementType && item.element.id === id
	);
	if (currentIndex === -1) return;

	let targetIndex = currentIndex;
	if (operation === 'forward') {
		targetIndex = Math.min(currentIndex + 1, combined.length - 1);
	} else if (operation === 'backward') {
		targetIndex = Math.max(currentIndex - 1, 0);
	} else if (operation === 'front') {
		targetIndex = combined.length - 1;
	} else if (operation === 'back') {
		targetIndex = 0;
	}

	if (targetIndex === currentIndex) return;

	const [moved] = combined.splice(currentIndex, 1);
	combined.splice(targetIndex, 0, moved);

	combined.forEach(({ element }, index) => {
		element.layerIndex = index;
	});

	state.textElements[surfaceId] = combined
		.filter((item) => item.type === 'text')
		.map((item) => item.element as TextElement);
	state.imageElements[surfaceId] = combined
		.filter((item) => item.type === 'image')
		.map((item) => item.element as ImageElement);
};

export const createCreatorInitialState = (snapshot?: CreatorSnapshot): CreatorState => {
	const base: CreatorState = {
		options: {
			currentStep: CreatorStep.CONFIGURATION,
			isProductTypeInfoOpened: false,
			isFullPreviewOpened: false,
			viewType: ViewType['3D'],
			layerType: LayerType.EXTERNAL,
			isSideView: false,
			selectedDecor: null,
			activeSurfaceId: null,
			activeSurfaceAxes: null,
			hoverSurfaceId: null,
			surfaceViewMode: 'NET',
			selectedElement: null,
			isBoxOpened: false,
			personalizationMode: 'designer',
		},
		stage: CreatorStage.ENTRY,
		selectedProductTypeId: null,
		selectedProductBoxStyle: 'box-flap',
		selectedVariantId: null,
		pendingPresetAttributes: null,
		configuration: {},
		pricing: createInitialPricingState(),
		textElements: {},
		imageElements: {},
		uploads: [],
		autosave: {
			lastSavedAt: null,
			lastRestoredAt: null,
			restoredFromSnapshot: false,
			lastChangeAt: null,
			lastSnackbarAt: null,
		},
		settings: createDefaultSettings(),
		apiProjectId: null,
		submission: {
			status: 'idle',
			error: null,
		},
		isTemplateEditor: false,
		templateRequestId: null,
		isViewOnly: false,
		quoteSummaryRows: null,
	};

	if (!snapshot) {
		normalizeAllSurfaceLayers(base);
		return base;
	}

	const nextState: CreatorState = {
		...base,
		settings: { ...base.settings },
		apiProjectId: snapshot?.apiProjectId ?? null,
		stage: snapshot.selectedProductTypeId ? CreatorStage.CREATOR : base.stage,
		selectedProductTypeId: snapshot.selectedProductTypeId ?? base.selectedProductTypeId,
		selectedProductBoxStyle: snapshot.selectedProductBoxStyle ?? base.selectedProductBoxStyle,
		selectedVariantId: snapshot.selectedVariantId ?? base.selectedVariantId,
		configuration: snapshot.configuration ?? base.configuration,
		textElements: snapshot.textElements
			? cloneSurfaceTextMap(snapshot.textElements)
			: base.textElements,
		imageElements: snapshot.imageElements
			? cloneSurfaceImageMap(snapshot.imageElements)
			: base.imageElements,
		uploads: snapshot.uploads
			? snapshot.uploads.map((upload) => ({ ...upload }))
			: base.uploads,
		options: {
			...base.options,
			...snapshot.options,
			isProductTypeInfoOpened: false,
			isFullPreviewOpened: false,
			personalizationMode:
				snapshot.options?.personalizationMode ?? base.options.personalizationMode,
		},
		pricing: snapshot.pricing
			? { status: 'succeeded', breakdown: snapshot.pricing, error: null, requiresQuote: false }
			: base.pricing,
	autosave: {
			lastSavedAt: snapshot.savedAt ?? null,
			lastRestoredAt: snapshot.savedAt ?? Date.now(),
			restoredFromSnapshot: Boolean(snapshot.savedAt),
			lastChangeAt: snapshot.savedAt ?? null,
			lastSnackbarAt: null,
		},
		submission: {
			status: 'idle',
			error: null,
		},
		isTemplateEditor: snapshot.isTemplateEditor ?? base.isTemplateEditor,
		templateRequestId: snapshot.templateRequestId ?? base.templateRequestId,
		isViewOnly: snapshot.isViewOnly ?? base.isViewOnly,
		quoteSummaryRows: null,
	};

	// Jeśli snapshot dotyczy torby, wymuś ustawienia zewnętrzne.
	const sanitized = enforceExternalForPaperBag(nextState, {});
	Object.assign(nextState.options, sanitized);

	normalizeAllSurfaceLayers(nextState);
	return nextState;
};

const persistedSnapshot =
	typeof window !== 'undefined' ? loadCreatorSnapshot() : null;

const initialState = createCreatorInitialState(persistedSnapshot ?? undefined);

export const creatorSlice = createSlice({
	name: StoreKey.CREATOR,
	initialState,
	reducers: {
		setApiProjectId: (state, { payload }: PayloadAction<string | null>) => {
			state.apiProjectId = payload;
			if (!payload) {
				state.submission = { status: 'idle', error: null };
			}
		},
		setSubmissionStatus: (
			state,
			{ payload }: PayloadAction<{ status: 'idle' | 'pending' | 'success' | 'error'; error?: string | null }>
		) => {
			state.submission.status = payload.status;
			state.submission.error = payload.status === 'error' ? payload.error ?? null : null;
		},
		resetCreatorState: (state) => {
			const next = createCreatorInitialState();
			return next;
		},
		setCreatorSettings: (
			state,
			{ payload }: PayloadAction<Partial<{ autosaveInterval: number; uploadMaxSize: number; fallbackTemplateIds: string[] }>>
		) => {
			if (typeof payload.autosaveInterval === 'number' && payload.autosaveInterval > 0) {
				state.settings.autosaveInterval = payload.autosaveInterval;
			}
			if (typeof payload.uploadMaxSize === 'number' && payload.uploadMaxSize > 0) {
				state.settings.uploadMaxSize = payload.uploadMaxSize;
			}
			if (payload.fallbackTemplateIds) {
				state.settings.fallbackTemplateIds = payload.fallbackTemplateIds;
			}
		},
		setCreatorStep: (state, { payload }: PayloadAction<'prev' | 'next'>) => {
			if (state.isTemplateEditor) {
				state.options.currentStep = CreatorStep.PERSONALIZATION;
				return;
			}
			payload === 'prev'
				? state.options.currentStep--
				: state.options.currentStep++;
		},
		setTemplateEditorMode: (
			state,
			{ payload }: PayloadAction<{ enabled: boolean; requestId?: string | null }>
		) => {
			state.isTemplateEditor = payload.enabled;
			state.templateRequestId = payload.requestId ?? null;
			if (payload.enabled) {
				state.options.currentStep = CreatorStep.PERSONALIZATION;
			}
		},
		setViewOnlyMode: (state, { payload }: PayloadAction<boolean>) => {
			state.isViewOnly = payload;
			if (payload) {
				state.options.selectedElement = null;
				state.options.selectedDecor = null;
			}
		},
		setQuoteSummaryRows: (
			state,
			{ payload }: PayloadAction<{ label: string; value: string }[] | null>
		) => {
			state.quoteSummaryRows = payload;
		},
	updateOptions: (
		state,
		{ payload }: PayloadAction<Partial<CreatorOptions>>
	) => {
		const sanitized = enforceExternalForPaperBag(state, payload);
		Object.assign(state.options, sanitized);
		if (state.isTemplateEditor) {
			state.options.currentStep = CreatorStep.PERSONALIZATION;
		}
	},
	setSelectedElement: (
		state,
		{ payload }: PayloadAction<CreatorOptions['selectedElement']>
	) => {
		state.options.selectedElement = payload;
	},
		setHoverSurface: (state, { payload }: PayloadAction<string | null>) => {
			if (state.options.hoverSurfaceId === payload) return;
			state.options.hoverSurfaceId = payload;
		},
		setTextElements: (
		state,
		{ payload }: PayloadAction<SurfaceTextMap>
	) => {
		state.textElements = cloneSurfaceTextMap(payload);
		normalizeAllSurfaceLayers(state);
	},
	setImageElements: (state, { payload }: PayloadAction<SurfaceImageMap>) => {
		state.imageElements = cloneSurfaceImageMap(payload);
		normalizeAllSurfaceLayers(state);
	},
	addTextElement: (
		state,
		{ payload }: PayloadAction<TextElement>
	) => {
		const next = ensureTextElementDefaults(payload);
		if (next.layerIndex < 0) {
			next.layerIndex = getNextLayerIndex(state, next.surfaceId);
		}
		const list = state.textElements[next.surfaceId] ?? [];

		const repeatCount = getTapeRepeatCount(state);
		if (
			repeatCount > 1 &&
			state.selectedProductBoxStyle === 'tape' &&
			isTapeSurfaceId(next.surfaceId) &&
			!next.repeatGroupId
		) {
			const groupId = next.id;
			const segmentIndex = getTapeSegmentIndex(next.position.x, repeatCount);
			let localX = getTapeLocalX(next.position.x, segmentIndex, repeatCount);
			if (localX < 0.0001 || localX > 0.9999) localX = 0.5;
			const y = clamp01(next.position.y);
			const usedIds = new Set<string>(list.map((el) => el.id));
			usedIds.add(next.id);

			const groupElements: TextElement[] = [];
			for (let i = 0; i < repeatCount; i += 1) {
				if (i === segmentIndex) {
					groupElements.push({
						...next,
						repeatGroupId: groupId,
						repeatIndex: i,
						position: { x: getTapeGlobalX(localX, i, repeatCount), y },
					});
					continue;
				}
				const id = getUniqueRepeatId(groupId, i, usedIds);
				usedIds.add(id);
				groupElements.push({
					...next,
					id,
					repeatGroupId: groupId,
					repeatIndex: i,
					position: { x: getTapeGlobalX(localX, i, repeatCount), y },
				});
			}

			state.textElements[next.surfaceId] = [...list, ...groupElements];
			normalizeSurfaceLayers(state, next.surfaceId);
			return;
		}

		state.textElements[next.surfaceId] = [...list, next];
		normalizeSurfaceLayers(state, next.surfaceId);
	},
	addImageElement: (state, { payload }: PayloadAction<ImageElement>) => {
		const next = ensureImageElementDefaults(payload);
		if (next.layerIndex < 0) {
			next.layerIndex = getNextLayerIndex(state, next.surfaceId);
		}
		const list = state.imageElements[next.surfaceId] ?? [];

		const repeatCount = getTapeRepeatCount(state);
		if (
			repeatCount > 1 &&
			state.selectedProductBoxStyle === 'tape' &&
			isTapeSurfaceId(next.surfaceId) &&
			!next.repeatGroupId
		) {
			const groupId = next.id;
			const segmentIndex = getTapeSegmentIndex(next.position.x, repeatCount);
			let localX = getTapeLocalX(next.position.x, segmentIndex, repeatCount);
			if (localX < 0.0001 || localX > 0.9999) localX = 0.5;
			const y = clamp01(next.position.y);
			const usedIds = new Set<string>(list.map((el) => el.id));
			usedIds.add(next.id);

			const groupElements: ImageElement[] = [];
			for (let i = 0; i < repeatCount; i += 1) {
				if (i === segmentIndex) {
					groupElements.push({
						...next,
						repeatGroupId: groupId,
						repeatIndex: i,
						position: { x: getTapeGlobalX(localX, i, repeatCount), y },
					});
					continue;
				}
				const id = getUniqueRepeatId(groupId, i, usedIds);
				usedIds.add(id);
				groupElements.push({
					...next,
					id,
					repeatGroupId: groupId,
					repeatIndex: i,
					position: { x: getTapeGlobalX(localX, i, repeatCount), y },
				});
			}

			state.imageElements[next.surfaceId] = [...list, ...groupElements];
			normalizeSurfaceLayers(state, next.surfaceId);
			return;
		}

		state.imageElements[next.surfaceId] = [...list, next];
		normalizeSurfaceLayers(state, next.surfaceId);
	},
	updateTextElement: (
		state,
		{
			payload,
		}: PayloadAction<{
			surfaceId: string;
			id: string;
			changes: Partial<Omit<TextElement, 'id' | 'surfaceId'>>;
		}>
	) => {
		const list = state.textElements[payload.surfaceId];
		if (!list) return;
		const target = list.find((element) => element.id === payload.id) ?? null;
		const repeatCount = getTapeRepeatCount(state);
		const groupId =
			state.selectedProductBoxStyle === 'tape' &&
			repeatCount > 1 &&
			isTapeSurfaceId(payload.surfaceId)
				? (target?.repeatGroupId ?? null)
				: null;

		if (!groupId) {
			state.textElements[payload.surfaceId] = list.map((element) =>
				element.id === payload.id ? { ...element, ...payload.changes } : element
			);
			if (payload.changes.layerIndex !== undefined) {
				normalizeSurfaceLayers(state, payload.surfaceId);
			}
			return;
		}

		const { position: positionChange, ...otherChanges } = payload.changes;
		const nextPosition = positionChange ?? target?.position ?? null;
		const anchorIndex =
			typeof target?.repeatIndex === 'number'
				? clamp(target.repeatIndex, 0, repeatCount - 1)
				: target
					? getTapeSegmentIndex(target.position.x, repeatCount)
					: 0;
		const localX =
			nextPosition && repeatCount > 0
				? getTapeLocalX(nextPosition.x, anchorIndex, repeatCount)
				: null;
		const y = nextPosition ? clamp01(nextPosition.y) : null;

		state.textElements[payload.surfaceId] = list.map((element) => {
			if (element.repeatGroupId !== groupId) return element;
			const repeatIndex =
				typeof element.repeatIndex === 'number'
					? clamp(element.repeatIndex, 0, repeatCount - 1)
					: getTapeSegmentIndex(element.position.x, repeatCount);
			const position =
				localX !== null && y !== null
					? { x: getTapeGlobalX(localX, repeatIndex, repeatCount), y }
					: element.position;
			return { ...element, ...otherChanges, position };
		});

		if (payload.changes.layerIndex !== undefined) {
			normalizeSurfaceLayers(state, payload.surfaceId);
		}
	},
	removeTextElement: (
		state,
		{ payload }: PayloadAction<{ surfaceId: string; id: string }>
	) => {
	const list = state.textElements[payload.surfaceId];
	if (!list) return;
	const target = list.find((element) => element.id === payload.id) ?? null;
	const shouldRemoveGroup =
		state.selectedProductBoxStyle === 'tape' &&
		isTapeSurfaceId(payload.surfaceId) &&
		Boolean(target?.repeatGroupId);
	const removedIds = new Set<string>();
	if (shouldRemoveGroup && target?.repeatGroupId) {
		state.textElements[payload.surfaceId] = list.filter((element) => {
			const shouldKeep = element.repeatGroupId !== target.repeatGroupId;
			if (!shouldKeep) removedIds.add(element.id);
			return shouldKeep;
		});
	} else {
		state.textElements[payload.surfaceId] = list.filter((element) => element.id !== payload.id);
		removedIds.add(payload.id);
	}
	normalizeSurfaceLayers(state, payload.surfaceId);
	if (
		state.options.selectedElement?.type === 'text' &&
		state.options.selectedElement.surfaceId === payload.surfaceId &&
		removedIds.has(state.options.selectedElement.id)
	) {
		state.options.selectedElement = null;
	}
	},
	restoreTextElement: (
		state,
		{ payload }: PayloadAction<{ element: TextElement; index: number }>
	) => {
		const restored = ensureTextElementDefaults(payload.element);
		if (payload.element.layerIndex !== undefined) {
			restored.layerIndex = payload.element.layerIndex;
		}
		const list = state.textElements[restored.surfaceId] ?? [];
		const insertionIndex = Math.min(Math.max(payload.index, 0), list.length);
		const nextList = [...list];
		nextList.splice(insertionIndex, 0, restored);
		state.textElements[restored.surfaceId] = nextList;
		normalizeSurfaceLayers(state, restored.surfaceId);
		if (state.selectedProductBoxStyle === 'tape' && isTapeSurfaceId(restored.surfaceId)) {
			normalizeTapeRepeatGroups(state);
		}
	},
	updateImageElement: (
		state,
		{
			payload,
		}: PayloadAction<{
			surfaceId: string;
			id: string;
			changes: Partial<Omit<ImageElement, 'id' | 'surfaceId'>>;
		}>
	) => {
		const list = state.imageElements[payload.surfaceId];
		if (!list) return;
		const target = list.find((element) => element.id === payload.id) ?? null;
		const repeatCount = getTapeRepeatCount(state);
		const groupId =
			state.selectedProductBoxStyle === 'tape' &&
			repeatCount > 1 &&
			isTapeSurfaceId(payload.surfaceId)
				? (target?.repeatGroupId ?? null)
				: null;

		if (!groupId) {
			state.imageElements[payload.surfaceId] = list.map((element) =>
				element.id === payload.id ? { ...element, ...payload.changes } : element
			);
			if (payload.changes.layerIndex !== undefined) {
				normalizeSurfaceLayers(state, payload.surfaceId);
			}
			return;
		}

		const { position: positionChange, ...otherChanges } = payload.changes;
		const nextPosition = positionChange ?? target?.position ?? null;
		const anchorIndex =
			typeof target?.repeatIndex === 'number'
				? clamp(target.repeatIndex, 0, repeatCount - 1)
				: target
					? getTapeSegmentIndex(target.position.x, repeatCount)
					: 0;
		const localX =
			nextPosition && repeatCount > 0
				? getTapeLocalX(nextPosition.x, anchorIndex, repeatCount)
				: null;
		const y = nextPosition ? clamp01(nextPosition.y) : null;

		state.imageElements[payload.surfaceId] = list.map((element) => {
			if (element.repeatGroupId !== groupId) return element;
			const repeatIndex =
				typeof element.repeatIndex === 'number'
					? clamp(element.repeatIndex, 0, repeatCount - 1)
					: getTapeSegmentIndex(element.position.x, repeatCount);
			const position =
				localX !== null && y !== null
					? { x: getTapeGlobalX(localX, repeatIndex, repeatCount), y }
					: element.position;
			return { ...element, ...otherChanges, position };
		});

		if (payload.changes.layerIndex !== undefined) {
			normalizeSurfaceLayers(state, payload.surfaceId);
		}
	},
	removeImageElement: (
		state,
		{ payload }: PayloadAction<{ surfaceId: string; id: string }>
	) => {
	const list = state.imageElements[payload.surfaceId];
	if (!list) return;
	const target = list.find((element) => element.id === payload.id) ?? null;
	const shouldRemoveGroup =
		state.selectedProductBoxStyle === 'tape' &&
		isTapeSurfaceId(payload.surfaceId) &&
		Boolean(target?.repeatGroupId);
	const removedIds = new Set<string>();
	if (shouldRemoveGroup && target?.repeatGroupId) {
		state.imageElements[payload.surfaceId] = list.filter((element) => {
			const shouldKeep = element.repeatGroupId !== target.repeatGroupId;
			if (!shouldKeep) removedIds.add(element.id);
			return shouldKeep;
		});
	} else {
		state.imageElements[payload.surfaceId] = list.filter((element) => element.id !== payload.id);
		removedIds.add(payload.id);
	}
	normalizeSurfaceLayers(state, payload.surfaceId);
	if (
		state.options.selectedElement?.type === 'image' &&
		state.options.selectedElement.surfaceId === payload.surfaceId &&
		removedIds.has(state.options.selectedElement.id)
	) {
		state.options.selectedElement = null;
	}
	},
	restoreImageElement: (
		state,
		{ payload }: PayloadAction<{ element: ImageElement; index: number }>
	) => {
		const restored = ensureImageElementDefaults(payload.element);
		if (payload.element.layerIndex !== undefined) {
			restored.layerIndex = payload.element.layerIndex;
		}
		const list = state.imageElements[restored.surfaceId] ?? [];
		const insertionIndex = Math.min(Math.max(payload.index, 0), list.length);
		const nextList = [...list];
		nextList.splice(insertionIndex, 0, restored);
		state.imageElements[restored.surfaceId] = nextList;
		normalizeSurfaceLayers(state, restored.surfaceId);
		if (state.selectedProductBoxStyle === 'tape' && isTapeSurfaceId(restored.surfaceId)) {
			normalizeTapeRepeatGroups(state);
		}
	},
	moveSurfaceElementLayer: (
		state,
		{
			payload,
		}: PayloadAction<{
			surfaceId: string;
			id: string;
			elementType: 'text' | 'image';
			operation: 'forward' | 'backward' | 'front' | 'back';
		}>
	) => {
		applySurfaceLayerOperation(state, payload);
	},
	setUploads: (state, { payload }: PayloadAction<ProjectUpload[]>) => {
		state.uploads = payload;
	},
	addUploadFile: (state, { payload }: PayloadAction<ProjectUpload>) => {
		const existing = state.uploads.find((upload) => upload.id === payload.id);
		if (existing) {
			Object.assign(existing, payload);
			return;
		}
		state.uploads.push(payload);
	},
		removeUploadFile: (state, { payload }: PayloadAction<string>) => {
			state.uploads = state.uploads.filter((upload) => upload.id !== payload);
		},
		clearUploadFiles: (state) => {
			state.uploads = [];
	},
	setStage: (state, { payload }: PayloadAction<CreatorStage>) => {
			state.stage = payload;
			if (payload === CreatorStage.DEFAULT_VARIANTS) {
				state.options.currentStep = CreatorStep.CONFIGURATION;
				state.options.isFullPreviewOpened = false;
				state.options.isSideView = false;
				state.options.activeSurfaceId = null;
				state.options.activeSurfaceAxes = null;
				state.options.hoverSurfaceId = null;
				state.options.surfaceViewMode = 'NET';
				state.options.selectedElement = null;
			}
			if (payload === CreatorStage.ENTRY) {
				state.options.currentStep = CreatorStep.CONFIGURATION;
				state.options.isSideView = false;
				state.options.selectedDecor = null;
				state.options.isBoxOpened = false;
				state.options.isFullPreviewOpened = false;
			state.options.activeSurfaceId = null;
			state.options.activeSurfaceAxes = null;
			state.options.hoverSurfaceId = null;
			state.options.surfaceViewMode = 'NET';
			state.options.selectedElement = null;
			state.options.personalizationMode = 'designer';
			state.selectedVariantId = null;
				state.pendingPresetAttributes = null;
				state.configuration = {};
				state.pricing = createInitialPricingState();
				state.textElements = {};
				state.imageElements = {};
				state.uploads = [];
			}
		},
	setSelectedProductType: (
		state,
		{ payload }: PayloadAction<string | null>
	) => {
		state.selectedProductTypeId = payload;
		state.selectedProductBoxStyle = 'box-flap';
		state.selectedVariantId = null;
		state.pendingPresetAttributes = null;
		state.configuration = {};
		state.pricing = createInitialPricingState();
		state.options.isBoxOpened = false;
		state.options.isFullPreviewOpened = false;
		state.options.activeSurfaceId = null;
	state.options.activeSurfaceAxes = null;
	state.options.hoverSurfaceId = null;
	state.options.surfaceViewMode = 'NET';
	state.options.selectedElement = null;
	state.options.personalizationMode = 'designer';
		state.textElements = {};
		state.imageElements = {};
		state.uploads = [];
	},
	setSelectedProductBoxStyle: (state, { payload }: PayloadAction<BoxModelStyle>) => {
		state.selectedProductBoxStyle = payload;
		// Wymuś widok zewnętrzny dla toreb i taśm (brak warstwy wewnętrznej).
		if (payload === 'paper-bag' || payload === 'tape') {
			const sanitized = enforceExternalForPaperBag(state, {});
			Object.assign(state.options, sanitized);
		}
	},
		setSelectedVariant: (
			state,
			{
				payload,
			}: PayloadAction<{
					id: string | null;
					presetAttributes?: Record<string, string | number | (string | number)[]> | null;
				}>
		) => {
			const variantChanged = state.selectedVariantId !== payload.id;
			state.selectedVariantId = payload.id;
			// Nakładaj domyślne atrybuty wariantu tylko po realnej zmianie wariantu.
			// Dzięki temu powrót z zakładki Personalizacja do Konfiguracja nie nadpisze
			// już wyklikanego modelu, jeśli jakiś kod ponownie wyśle setSelectedVariant
			// z tym samym id (np. po refetchu danych).
			if (variantChanged) {
				state.pendingPresetAttributes = payload.presetAttributes ?? null;
				if (payload.presetAttributes) {
					Object.entries(payload.presetAttributes).forEach(([key, value]) => {
						state.configuration[key] = getPresetAttributeValue(value);
					});
				}
			}
		},
		overridePendingPresetAttributes: (
			state,
			{ payload }: PayloadAction<Record<string, string | number | (string | number)[]>>,
		) => {
			state.pendingPresetAttributes = payload;
		},
		setConfigurationValue: (
			state,
			{
				payload,
			}: PayloadAction<{
				attributeId: string;
				value: string | number | null;
			}>
		) => {
			state.configuration[payload.attributeId] = payload.value;
			if (payload.attributeId === TAPE_REPEAT_ATTRIBUTE_ID) {
				normalizeTapeRepeatGroups(state);
			}

			const isSizeModeKey =
				payload.attributeId === 'sizeMode' ||
				payload.attributeId.toLowerCase().includes('sizemode');
			if (isSizeModeKey && String(payload.value) === 'custom') {
				const existingWidth = Number(state.configuration.customWidth) || 0;
				const existingDepth = Number(state.configuration.customDepth) || 0;
				const existingHeight = Number(state.configuration.customHeight) || 0;
				if (existingWidth > 0 && existingDepth > 0 && existingHeight > 0) {
					return;
				}

				const sizeCandidates: Array<string | number | null | undefined> = [
					state.configuration.size,
					state.configuration.format,
				];
				Object.values(state.configuration).forEach((value) => {
					if (typeof value === 'string' || typeof value === 'number') {
						sizeCandidates.push(value);
					}
				});

				let parsed = null as ReturnType<typeof parseSizeForBoxStyle>;
				for (const candidate of sizeCandidates) {
					parsed = parseSizeForBoxStyle(state.selectedProductBoxStyle, candidate);
					if (parsed) break;
				}

				if (!parsed) return;
				if (existingWidth <= 0) state.configuration.customWidth = parsed.width;
				if (existingDepth <= 0) state.configuration.customDepth = parsed.depth;
				if (existingHeight <= 0) state.configuration.customHeight = parsed.height;
			}
		},
		setConfiguration: (
			state,
			{ payload }: PayloadAction<Record<string, string | number | null>>
		) => {
			state.configuration = payload;
			if (Object.prototype.hasOwnProperty.call(payload, TAPE_REPEAT_ATTRIBUTE_ID)) {
				normalizeTapeRepeatGroups(state);
			}
		},
		consumePresetAttributes: (state) => {
			if (!state.pendingPresetAttributes) return;
			const updates: Record<string, string | number | null> = {};
			Object.entries(state.pendingPresetAttributes).forEach(([key, value]) => {
				if (Array.isArray(value)) {
					updates[key] = (value[0] as string | number | undefined) ?? null;
				} else {
					updates[key] = (value as string | number | null) ?? null;
				}
			});

			// Jeśli brak sizeMode, domyślnie traktujemy jako gotowy/preset.
			if (!updates.sizeMode) {
				updates.sizeMode = 'preset';
			}

			const sizeString =
				typeof updates.size === 'string' && updates.size.trim().length
					? updates.size
					: typeof updates.format === 'string' && updates.format.trim().length
						? updates.format
						: null;

			if (updates.sizeMode === 'custom' && sizeString) {
				const parsed = parseSizeForBoxStyle(state.selectedProductBoxStyle, sizeString);
				if (parsed) {
					updates.customWidth = parsed.width;
					updates.customDepth = parsed.depth;
					updates.customHeight = parsed.height;
				}
			}

			if (updates.sizeMode !== 'custom') {
				updates.customWidth = null;
				updates.customDepth = null;
				updates.customHeight = null;
				if (!updates.size && sizeString) {
					updates.size = sizeString;
				}
			}


			// Nakładamy domyślne atrybuty wariantu tylko na brakujące pola, nie nadpisujemy
			// wartości, które użytkownik już zmienił (np. po przejściu do personalizacji
			// i powrocie do konfiguracji).
			const merged: Record<string, string | number | null> = {
				...state.configuration,
			};

			Object.entries(updates).forEach(([key, value]) => {
				const current = merged[key];
				const isEmpty =
					current === undefined ||
					current === null ||
					(typeof current === 'string' && current.trim().length === 0);
				if (isEmpty) {
					merged[key] = value;
				}
			});

			// Jeśli preset wymusza tryb gotowy (nie custom), zerujemy wartości custom
			// wyłącznie wtedy, gdy w konfiguracji nie ma już własnych wartości.
			const presetSizeMode = updates.sizeMode ?? merged.sizeMode;
			if (presetSizeMode !== 'custom') {
				if (merged.customWidth === undefined || merged.customWidth === null) {
					merged.customWidth = null;
				}
				if (merged.customDepth === undefined || merged.customDepth === null) {
					merged.customDepth = null;
				}
				if (merged.customHeight === undefined || merged.customHeight === null) {
					merged.customHeight = null;
				}
			}

			state.configuration = merged;
			state.pendingPresetAttributes = null;
		},
		setPricingStatus: (
			state,
			{ payload }: PayloadAction<CreatorPricingState['status']>
		) => {
			state.pricing.status = payload;
			if (payload === 'idle') {
				state.pricing.error = null;
				state.pricing.requiresQuote = false;
			}
			if (payload === 'loading') {
				state.pricing.error = null;
				state.pricing.requiresQuote = false;
			}
		},
	setPricingBreakdown: (
		state,
		{ payload }: PayloadAction<PricingBreakdown | null>
	) => {
		state.pricing.breakdown = payload;
		state.pricing.status = payload ? 'succeeded' : 'idle';
		if (!payload) state.pricing.error = null;
		if (payload) state.pricing.requiresQuote = false;
	},
	setPricingQuoteRequired: (state, { payload }: PayloadAction<boolean>) => {
		state.pricing.requiresQuote = payload;
		if (payload) {
			state.pricing.breakdown = null;
			state.pricing.error = null;
			state.pricing.status = 'idle';
		}
	},
	setPricingError: (state, { payload }: PayloadAction<string | null>) => {
		state.pricing.error = payload;
		state.pricing.status = payload ? 'failed' : 'idle';
		if (payload) state.pricing.breakdown = null;
		if (payload) state.pricing.requiresQuote = false;
	},
	markAutosaveSaved: (state, { payload }: PayloadAction<number>) => {
		state.autosave.lastSavedAt = payload;
		state.autosave.restoredFromSnapshot = false;
	},
	markAutosaveChange: (state, { payload }: PayloadAction<number>) => {
		state.autosave.lastChangeAt = payload;
	},
	markAutosaveSnackbarShown: (state, { payload }: PayloadAction<number>) => {
		state.autosave.lastSnackbarAt = payload;
	},
	resetAutosaveMeta: (state) => {
		state.autosave = {
			lastSavedAt: null,
			lastRestoredAt: null,
			restoredFromSnapshot: false,
			lastChangeAt: null,
			lastSnackbarAt: null,
		};
	},
	resetCreator: () => createCreatorInitialState(),
	resetCurrentProject: (
		state,
		{
			payload,
		}: PayloadAction<
			| {
					presetAttributes?: Record<
						string,
						string | number | (string | number)[]
					> | null;
			  }
			| undefined
		>
	) => {
		const preserved = {
			selectedProductTypeId: state.selectedProductTypeId,
			selectedProductBoxStyle: state.selectedProductBoxStyle,
			selectedVariantId: state.selectedVariantId,
			settings: state.settings,
			isTemplateEditor: state.isTemplateEditor,
			templateRequestId: state.templateRequestId,
		};

		const next = createCreatorInitialState();
		next.stage = CreatorStage.CREATOR;
		next.selectedProductTypeId = preserved.selectedProductTypeId;
		next.selectedProductBoxStyle = preserved.selectedProductBoxStyle;
		next.selectedVariantId = preserved.selectedVariantId;
		next.settings = preserved.settings;
		next.isTemplateEditor = preserved.isTemplateEditor;
		next.templateRequestId = preserved.templateRequestId;
		next.apiProjectId = null;
		next.submission = { status: 'idle', error: null };

		const presetAttributes = payload?.presetAttributes ?? null;
		if (presetAttributes) {
			next.pendingPresetAttributes = presetAttributes;
			Object.entries(presetAttributes).forEach(([key, value]) => {
				next.configuration[key] = getPresetAttributeValue(value);
			});
		}

		if (
			next.selectedProductBoxStyle === 'paper-bag' ||
			next.selectedProductBoxStyle === 'tape'
		) {
			const sanitized = enforceExternalForPaperBag(next, {});
			Object.assign(next.options, sanitized);
		}

		normalizeAllSurfaceLayers(next);
		return next;
	},
	},
});

export const creatorActions = creatorSlice.actions;

export const selectCreatorState = <State extends { creator: CreatorState }>(state: State) =>
	state.creator;
export default creatorSlice.reducer;
